import { useEffect, useState } from 'react';
import { CalendarClock, Loader2, MapPin, Pencil, Plus, Power, Users } from 'lucide-react';
import { toast } from 'sonner';
import { subeventsApi, Subevent } from '../../src/api/subevents';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

interface SubeventsManagerProps {
  event: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SubeventFormState {
  name: string;
  description: string;
  location: string;
  startTime: string;
  endTime: string;
  capacity: string;
  isActive: boolean;
}

const EMPTY_FORM: SubeventFormState = {
  name: '',
  description: '',
  location: '',
  startTime: '',
  endTime: '',
  capacity: '',
  isActive: true,
};

const CIVIL_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

const isValidCivilDateTime = (value: string): boolean => {
  const match = CIVIL_DATE_TIME_PATTERN.exec(value);
  if (!match) return false;

  const [, yearText, monthText, dayText, hourText, minuteText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysByMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  return month >= 1
    && month <= 12
    && day >= 1
    && day <= daysByMonth[month - 1]
    && hour >= 0
    && hour <= 23
    && minute >= 0
    && minute <= 59;
};

const toDateTimeLocal = (value: string): string => {
  const civilValue = value.slice(0, 16);
  return isValidCivilDateTime(civilValue) ? civilValue : '';
};

const toCivilApiDateTime = (value: string): string | null => (
  isValidCivilDateTime(value) ? `${value}:00.000Z` : null
);

const formatDateTime = (value: string): string => new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'UTC',
}).format(new Date(value));

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const responseMessage = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof responseMessage === 'string' ? responseMessage : fallback;
};

export function SubeventsManager({ event, open, onOpenChange }: SubeventsManagerProps) {
  const [subevents, setSubevents] = useState<Subevent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState<string | null>(null);
  const [editingSubevent, setEditingSubevent] = useState<Subevent | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SubeventFormState>(EMPTY_FORM);

  const loadSubevents = async (eventId: string) => {
    setIsLoading(true);
    try {
      setSubevents(await subeventsApi.list(eventId));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'No fue posible cargar los subeventos.'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !event) return;
    setEditingSubevent(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
    void loadSubevents(event.id);
  }, [open, event?.id]);

  const openCreateForm = () => {
    setEditingSubevent(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (subevent: Subevent) => {
    setEditingSubevent(subevent);
    setForm({
      name: subevent.name,
      description: subevent.description || '',
      location: subevent.location || '',
      startTime: toDateTimeLocal(subevent.startTime),
      endTime: toDateTimeLocal(subevent.endTime),
      capacity: subevent.capacity === null ? '' : String(subevent.capacity),
      isActive: subevent.isActive,
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setEditingSubevent(null);
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const saveSubevent = async () => {
    if (!event) return;
    const name = form.name.trim();
    if (!name || !form.startTime || !form.endTime) {
      toast.error('Nombre, fecha de inicio y fecha de fin son obligatorios.');
      return;
    }

    const startTime = toCivilApiDateTime(form.startTime);
    const endTime = toCivilApiDateTime(form.endTime);
    if (!startTime || !endTime || form.endTime <= form.startTime) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio.');
      return;
    }

    const capacity = form.capacity.trim() === '' ? null : Number(form.capacity);
    if (capacity !== null && (!Number.isInteger(capacity) || capacity <= 0)) {
      toast.error('El cupo debe ser un entero mayor que cero.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name,
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        startTime,
        endTime,
        capacity,
        isActive: form.isActive,
      };

      if (editingSubevent) {
        await subeventsApi.update(event.id, editingSubevent.id, payload);
        toast.success('Subevento actualizado.');
      } else {
        await subeventsApi.create(event.id, payload);
        toast.success('Subevento creado.');
      }

      cancelForm();
      await loadSubevents(event.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'No fue posible guardar el subevento.'));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleStatus = async (subevent: Subevent) => {
    if (!event) return;
    setStatusChangingId(subevent.id);
    try {
      await subeventsApi.updateStatus(event.id, subevent.id, !subevent.isActive);
      toast.success(subevent.isActive ? 'Subevento desactivado.' : 'Subevento reactivado.');
      await loadSubevents(event.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'No fue posible cambiar el estado del subevento.'));
    } finally {
      setStatusChangingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-hidden sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Gestionar subeventos</DialogTitle>
          <DialogDescription>
            Evento principal: <span className="font-semibold text-slate-700">{event?.name || '—'}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1 max-h-[calc(92vh-10rem)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Programa físico</p>
              <p className="text-xs text-slate-500">Cada elemento se guarda como una fila independiente.</p>
            </div>
            <Button onClick={openCreateForm} disabled={showForm} className="bg-[#1e3a5f] text-white hover:bg-[#152b47] hover:text-white disabled:text-white disabled:opacity-50">
              <Plus className="mr-2 h-4 w-4" /> Nuevo subevento
            </Button>
          </div>

          {showForm && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-semibold text-[#0f2744]">
                  {editingSubevent ? 'Editar subevento' : 'Nuevo subevento'}
                </h3>
                <Badge variant="outline">{editingSubevent ? 'Edición' : 'Alta'}</Badge>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2 sm:col-span-2">
                  <Label htmlFor="subevent-name">Nombre *</Label>
                  <Input
                    id="subevent-name"
                    value={form.name}
                    maxLength={300}
                    onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2 sm:col-span-2">
                  <Label htmlFor="subevent-description">Descripción</Label>
                  <Textarea
                    id="subevent-description"
                    value={form.description}
                    maxLength={10000}
                    onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="subevent-location">Sede</Label>
                  <Input
                    id="subevent-location"
                    value={form.location}
                    maxLength={300}
                    onChange={(e) => setForm((current) => ({ ...current, location: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="subevent-capacity">Cupo</Label>
                  <Input
                    id="subevent-capacity"
                    type="number"
                    min={1}
                    step={1}
                    value={form.capacity}
                    onChange={(e) => setForm((current) => ({ ...current, capacity: e.target.value }))}
                    placeholder="Sin límite"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="subevent-start">Fecha/hora de inicio *</Label>
                  <Input
                    id="subevent-start"
                    type="datetime-local"
                    value={form.startTime}
                    onChange={(e) => setForm((current) => ({ ...current, startTime: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="subevent-end">Fecha/hora de fin *</Label>
                  <Input
                    id="subevent-end"
                    type="datetime-local"
                    value={form.endTime}
                    onChange={(e) => setForm((current) => ({ ...current, endTime: e.target.value }))}
                  />
                </div>
                <div className="flex items-center justify-between rounded-md border bg-white p-3 sm:col-span-2">
                  <div>
                    <Label htmlFor="subevent-active">Activo</Label>
                    <p className="text-xs text-slate-500">Los subeventos inactivos se conservan en la base.</p>
                  </div>
                  <Switch
                    id="subevent-active"
                    checked={form.isActive}
                    onCheckedChange={(checked) => setForm((current) => ({ ...current, isActive: checked }))}
                    className="!h-8 !w-14 !rounded-full !border !border-slate-300 data-[state=checked]:!bg-[#1e3a5f] data-[state=unchecked]:!bg-slate-300 [&_[data-slot=switch-thumb]]:!size-6 [&_[data-slot=switch-thumb]]:!rounded-full [&_[data-slot=switch-thumb]]:!bg-white [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb][data-state=unchecked]]:!translate-x-[2px] [&_[data-slot=switch-thumb][data-state=checked]]:!translate-x-[30px]"
                  />
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={cancelForm} disabled={isSaving}>Cancelar</Button>
                <Button onClick={saveSubevent} disabled={isSaving} className="bg-[#1e3a5f] text-white hover:bg-[#152b47] hover:text-white disabled:text-white disabled:opacity-50">
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingSubevent ? 'Guardar cambios' : 'Crear subevento'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {!showForm && (
            isLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-[#1e3a5f]" /></div>
            ) : subevents.length === 0 ? (
              <div className="rounded-lg border border-dashed p-10 text-center text-sm text-slate-500">
                Este evento todavía no tiene subeventos.
              </div>
            ) : (
              <div className="space-y-3">
                {subevents.map((subevent) => (
                  <div key={subevent.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-semibold text-slate-900">{subevent.name}</h4>
                        <Badge className={subevent.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                          {subevent.isActive ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      {subevent.description && <p className="text-sm text-slate-600">{subevent.description}</p>}
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{formatDateTime(subevent.startTime)} – {formatDateTime(subevent.endTime)}</span>
                        {subevent.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{subevent.location}</span>}
                        <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{subevent.capacity ?? 'Sin límite'}</span>
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEditForm(subevent)}>
                        <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={statusChangingId === subevent.id}
                        onClick={() => toggleStatus(subevent)}
                        className={subevent.isActive ? 'text-red-600' : 'text-emerald-700'}
                      >
                        {statusChangingId === subevent.id
                          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          : <Power className="mr-1.5 h-3.5 w-3.5" />}
                        {subevent.isActive ? 'Desactivar' : 'Reactivar'}
                      </Button>
                    </div>
                  </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
